import React, { useState, useEffect } from 'react';
import { 
  Container, 
  Typography, 
  Paper, 
  Box, 
  TextField,
  Button,
  Grid,
  FormControl,
  Table,
  TableBody,
  TableCell,
  TableContainer,
  TableHead,
  TableRow,
  IconButton,
  Tooltip
} from '@mui/material';
import { Delete, Edit } from '@mui/icons-material';
import { collection, addDoc, getDocs, query, where, deleteDoc, doc, updateDoc } from 'firebase/firestore';
import { firestore } from './firebase.config';
import { useAuth } from './AuthContext';

const TenantManagement = () => {
  const [formData, setFormData] = useState({ 
    tenantId: '',
    tenantName: '',
    adminName: '',
    adminEmailId: '',
    status: 'active'
  });

  const [tenants, setTenants] = useState([]);
  const [errors, setErrors] = useState({});
  const [isSubmitting, setIsSubmitting] = useState(false);
  const [submitSuccess, setSubmitSuccess] = useState(false);
  const [selectedTenant, setSelectedTenant] = useState(null);
  const { currentUser } = useAuth();

  useEffect(() => {
    fetchTenants();
  }, []);

  const fetchTenants = async () => {
    try {
      const tenantsRef = collection(firestore, "Tenants");
      const querySnapshot = await getDocs(tenantsRef);
      const tenantsData = querySnapshot.docs.map(doc => ({
        id: doc.id,
        ...doc.data()
      }));
      setTenants(tenantsData);
    } catch (error) {
      console.error("Error fetching tenants:", error);
    }
  };

  const handleChange = (e) => {
    const { name, value } = e.target;
    setFormData(prevState => ({
      ...prevState,
      [name]: value
    }));
  };

  const validateForm = () => {
    let tempErrors = {};
    let isValid = true;

    if (!formData.tenantId) {
      tempErrors.tenantId = 'Tenant ID is required';
      isValid = false;
    }

    if (!formData.tenantName) {
      tempErrors.tenantName = 'Tenant Name is required';
      isValid = false;
    }

    if (!formData.adminName) {
      tempErrors.adminName = 'Admin Name is required';
      isValid = false;
    }

    if (!formData.adminEmailId) {
      tempErrors.adminEmailId = 'Admin Email ID is required';
      isValid = false;
    } else if (!/\S+@\S+\.\S+/.test(formData.adminEmailId)) {
      tempErrors.adminEmailId = 'Invalid email format';
      isValid = false;
    }

    setErrors(tempErrors);
    return isValid;
  };

  const handleSubmit = async (e) => {
    e.preventDefault();
    
    if (!validateForm()) {
      return;
    }

    setIsSubmitting(true);
    setErrors({});

    try {
      if (selectedTenant) {
        // Update existing tenant
        await updateDoc(doc(firestore, "Tenants", selectedTenant.id), {
          ...formData,
          updatedBy: currentUser.email,
          updatedAt: new Date().toISOString()
        });
      } else {
        // Check if tenant ID already exists
        const tenantsRef = collection(firestore, "Tenants");
        const q = query(tenantsRef, where("tenantId", "==", formData.tenantId));
        const querySnapshot = await getDocs(q);

        if (!querySnapshot.empty) {
          setErrors({
            tenantId: "Tenant ID already exists"
          });
          setIsSubmitting(false);
          return;
        }

        // Add new tenant
        await addDoc(collection(firestore, "Tenants"), {
          ...formData,
          createdBy: currentUser.email,
          createdAt: new Date().toISOString()
        });

        // Determine role for admin
        const superAdminEmails = [
          process.env.REACT_APP_ADMIN_EMAIL1,
          process.env.REACT_APP_ADMIN_EMAIL2,
          process.env.REACT_APP_ADMIN_EMAIL3
        ].filter(Boolean);
        const adminRole = superAdminEmails.includes(formData.adminEmailId) ? 'SuperAdmin' : 'AppAdmin';

        // Create UserProfileMaster document for the tenant admin
        await addDoc(collection(firestore, "UserProfileMaster"), {
          displayName: formData.adminName,
          email: formData.adminEmailId,
          tenantId: formData.tenantId,
          tenantName: formData.tenantName,
          role: adminRole,
          status: 'active',
          createdAt: new Date().toISOString()
        });
      }

      // Reset form after successful submission
      setFormData({
        tenantId: '',
        tenantName: '',
        adminName: '',
        adminEmailId: '',
        status: 'active'
      });
      setSelectedTenant(null);
      
      setSubmitSuccess(true);
      fetchTenants(); // Refresh the tenants list
      
      setTimeout(() => {
        setSubmitSuccess(false);
      }, 3000);
      
    } catch (error) {
      console.error("Error managing tenant: ", error);
      setErrors({
        submission: "Operation failed. Please try again."
      });
    } finally {
      setIsSubmitting(false);
    }
  };

  const handleEdit = (tenant) => {
    setSelectedTenant(tenant);
    setFormData({
      tenantId: tenant.tenantId,
      tenantName: tenant.tenantName,
      adminName: tenant.adminName,
      adminEmailId: tenant.adminEmailId,
      status: tenant.status
    });
  };

  const handleDelete = async (tenantId) => {
    if (window.confirm('Are you sure you want to delete this tenant? This action cannot be undone.')) {
      try {
        await deleteDoc(doc(firestore, "Tenants", tenantId));
        fetchTenants();
      } catch (error) {
        console.error("Error deleting tenant:", error);
        setErrors({
          submission: "Failed to delete tenant. Please try again."
        });
      }
    }
  };

  return (
    <Container maxWidth="lg">
      <Box sx={{ p: 3 }}>
        <Typography variant="h4" gutterBottom>
          Tenant Management
        </Typography>
        
        {submitSuccess && (
          <Paper 
            elevation={0} 
            sx={{ 
              p: 2, 
              mb: 3, 
              backgroundColor: '#e8f5e9',
              borderRadius: 1
            }}
          >
            <Typography color="success.main">
              {selectedTenant ? 'Tenant updated successfully!' : 'Tenant added successfully!'}
            </Typography>
          </Paper>
        )}

        {errors.submission && (
          <Paper 
            elevation={0} 
            sx={{ 
              p: 2, 
              mb: 3, 
              backgroundColor: '#ffebee',
              borderRadius: 1
            }}
          >
            <Typography color="error">
              {errors.submission}
            </Typography>
          </Paper>
        )}

        <Grid container spacing={3}>
          <Grid item xs={12}>
            <Paper sx={{ p: 3, mb: 3 }}>
              <form onSubmit={handleSubmit}>
                <Grid container spacing={3}>
                  <Grid item xs={12} sm={6}>
                    <FormControl fullWidth error={!!errors.tenantId}>
                      <TextField
                        label="Tenant ID"
                        name="tenantId"
                        value={formData.tenantId}
                        onChange={handleChange}
                        fullWidth
                        required
                        error={!!errors.tenantId}
                        helperText={errors.tenantId}
                        disabled={!!selectedTenant}
                      />
                    </FormControl>
                  </Grid>
                  <Grid item xs={12} sm={6}>
                    <FormControl fullWidth error={!!errors.tenantName}>
                      <TextField
                        label="Tenant Name"
                        name="tenantName"
                        value={formData.tenantName}
                        onChange={handleChange}
                        fullWidth
                        required
                        error={!!errors.tenantName}
                        helperText={errors.tenantName}
                      />
                    </FormControl>
                  </Grid>
                  <Grid item xs={12} sm={6}>
                    <FormControl fullWidth error={!!errors.adminName}>
                      <TextField
                        label="Admin Name"
                        name="adminName"
                        value={formData.adminName}
                        onChange={handleChange}
                        fullWidth
                        required
                        error={!!errors.adminName}
                        helperText={errors.adminName}
                      />
                    </FormControl>
                  </Grid>
                  <Grid item xs={12} sm={6}>
                    <FormControl fullWidth error={!!errors.adminEmailId}>
                      <TextField
                        label="Admin Email ID"
                        name="adminEmailId"
                        value={formData.adminEmailId}
                        onChange={handleChange}
                        fullWidth
                        required
                        error={!!errors.adminEmailId}
                        helperText={errors.adminEmailId}
                        type="email"
                      />
                    </FormControl>
                  </Grid>
                  <Grid item xs={12}>
                    <Button
                      type="submit"
                      variant="contained"
                      color="primary"
                      disabled={isSubmitting}
                      sx={{ mr: 2 }}
                    >
                      {selectedTenant ? 'Update Tenant' : 'Add Tenant'}
                    </Button>
                    {selectedTenant && (
                      <Button
                        variant="outlined"
                        onClick={() => {
                          setSelectedTenant(null);
                          setFormData({
                            tenantId: '',
                            tenantName: '',
                            adminName: '',
                            adminEmailId: '',
                            status: 'active'
                          });
                        }}
                      >
                        Cancel Edit
                      </Button>
                    )}
                  </Grid>
                </Grid>
              </form>
            </Paper>
          </Grid>

          <Grid item xs={12}>
            <TableContainer component={Paper}>
              <Table>
                <TableHead>
                  <TableRow>
                    <TableCell>Tenant ID</TableCell>
                    <TableCell>Tenant Name</TableCell>
                    <TableCell>Admin Name</TableCell>
                    <TableCell>Admin Email ID</TableCell>
                    <TableCell align="center">Actions</TableCell>
                  </TableRow>
                </TableHead>
                <TableBody>
                  {tenants.map((tenant) => (
                    <TableRow key={tenant.id}>
                      <TableCell>{tenant.tenantId}</TableCell>
                      <TableCell>{tenant.tenantName}</TableCell>
                      <TableCell>{tenant.adminName}</TableCell>
                      <TableCell>{tenant.adminEmailId}</TableCell>
                      <TableCell align="center">
                        <Tooltip title="Edit">
                          <IconButton color="primary" onClick={() => handleEdit(tenant)}>
                            <Edit />
                          </IconButton>
                        </Tooltip>
                        <Tooltip title="Delete">
                          <IconButton color="error" onClick={() => handleDelete(tenant.id)}>
                            <Delete />
                          </IconButton>
                        </Tooltip>
                      </TableCell>
                    </TableRow>
                  ))}
                </TableBody>
              </Table>
            </TableContainer>
          </Grid>
        </Grid>
      </Box>
    </Container>
  );
};

export default TenantManagement; 